import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@kss/ui';
import { publicCatalogService, type PublicPage } from '@kss/api';
import { Sparkles } from 'lucide-react';

/**
 * "Our Story" storefront page — reads the current tenant's active
 * PageContent row keyed on <code>slug === 'our-story'</code>. Falls back
 * to a friendly placeholder when no row exists so the site never looks
 * broken while an admin is still writing the content.
 *
 * Body is rendered via <code>dangerouslySetInnerHTML</code> — trusted
 * input, admin-authored, no cross-tenant data crossing the wire.
 */
export default function About() {
  const [page, setPage] = useState<PublicPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const data = await publicCatalogService.getPage('our-story');
        if (!cancelled) setPage(data);
      } catch (e) {
        // Network / tenant-resolution errors — swallow to the placeholder
        // rather than crashing the whole page. 404 already resolves to
        // `null` inside the service.
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const title = page?.title?.trim() || 'Our story';
  const body = page?.body?.trim() ?? '';
  const showPlaceholder = !loading && !body;

  return (
    <div className="bg-[var(--color-bg-app)]">
      {/* Hero */}
      <section className="border-b border-[var(--color-border)] bg-[var(--color-bg-topbar)]">
        <div className="mx-auto max-w-5xl px-4 py-12 text-center sm:px-6 sm:py-16">
          <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-primary-light)] px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[var(--color-primary)]">
            <Sparkles className="h-3.5 w-3.5" />
            About us
          </span>
          <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-[var(--color-text-primary)] sm:text-4xl">
            {title}
          </h1>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/shop"
              className="inline-flex h-10 items-center justify-center rounded-md bg-[var(--color-primary)] px-5 text-sm font-semibold text-[var(--color-primary-fg)] hover:bg-[var(--color-primary-hover)]"
            >
              Start shopping
            </Link>
            <Link
              to="/contact"
              className="inline-flex h-10 items-center justify-center rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-5 text-sm font-semibold text-[var(--color-text-primary)] hover:bg-[var(--color-bg-card-hover)]"
            >
              Contact us
            </Link>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="mx-auto max-w-4xl px-4 py-10 sm:px-6 sm:py-12">
        {loading ? (
          <Card className="p-6 text-center text-sm text-[var(--color-text-muted)]">
            Loading…
          </Card>
        ) : showPlaceholder ? (
          <Card className="p-8 text-center">
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
              Our story is coming soon
            </h2>
            <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
              We&apos;re still writing this page. Meanwhile, take a look at what we
              stock in the storefront.
            </p>
            {error && (
              <p className="mt-3 text-xs text-[var(--color-text-muted)]">
                (Load hint: {error})
              </p>
            )}
            <Link
              to="/shop"
              className="mt-5 inline-flex h-10 items-center justify-center rounded-md bg-[var(--color-primary)] px-5 text-sm font-semibold text-[var(--color-primary-fg)] hover:bg-[var(--color-primary-hover)]"
            >
              Browse the catalog
            </Link>
          </Card>
        ) : (
          <Card className="p-6 sm:p-8">
            {/* Body is admin-authored HTML or Markdown text. We render
                it raw — the panel form asks admins to paste plain HTML
                (the reasoning is written next to the textarea). */}
            <div
              className="page-content-body prose max-w-none text-sm leading-relaxed text-[var(--color-text-secondary)]"
              dangerouslySetInnerHTML={{ __html: body }}
            />
          </Card>
        )}
      </section>

      {/* Closing CTA — kept static so the About page always has a
          direction, even when the CMS body is short. */}
      <section className="mx-auto max-w-3xl px-4 pb-12 text-center sm:px-6 sm:pb-16">
        <h2 className="text-xl font-bold text-[var(--color-text-primary)] sm:text-2xl">
          Ready to start shopping?
        </h2>
        <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
          Browse the full catalog or jump straight into the latest arrivals.
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <Link
            to="/shop"
            className="inline-flex h-10 items-center justify-center rounded-md bg-[var(--color-primary)] px-5 text-sm font-semibold text-[var(--color-primary-fg)] hover:bg-[var(--color-primary-hover)]"
          >
            Browse all products
          </Link>
          <Link
            to="/shop?sort=new"
            className="inline-flex h-10 items-center justify-center rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-5 text-sm font-semibold text-[var(--color-text-primary)] hover:bg-[var(--color-bg-card-hover)]"
          >
            New arrivals
          </Link>
        </div>
      </section>
    </div>
  );
}
